import React, { useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import { useForm } from 'react-final-form';
import { Checkbox, Expandable } from '@haldor/ui';

const BlockCoreContentField = ({ input, ...props }) => {
	let values = [];
	if (input.value != null && input.value != "") {
		values = JSON.parse(JSON.stringify(input.value));
	}

	const forbiddenText = "Undervisningen i kursen ska behandla följande centrala innehåll:";
	const form = useForm();
	const currentRows = useRef([]);
	const lockedCoursesRef = useRef(new Set());

	const { assessmentBlockPartRows, courses } = useMemo(() => {
		const value = form.getFieldState('assessmentBlocks')?.value;
		if (value == null || value.length == 0) {
			return {
				courses: [],
				assessmentBlockPartRows: [],
			};
		}

		let courses = [];
		let blockPartRows = [];
		const blocks = value.filter((b) => b.type === 'Haldor.Blocks.AssessmentBlock');

		blocks.forEach((items) => {
			const resources = items.resources.filter((s) =>
				s["@odata.type"] === 'Haldor.Blocks.AssessmentBlockPart' && s.referenceType === 'matrix'
			);

			if (resources) {
				resources.forEach((resource) => {
					let course = JSON.parse(JSON.stringify(resource));
					let inCourses = courses.find((r) => r.value?.id == course.value?.id);
					if (inCourses == null) {
						let details = course?.value?.details.filter((x) =>
							x.endDate == null || moment(x.endDate).isAfter(moment())
						);
						course.value.details = details;
						courses.push(course.value);
					}

					resource.assessmentBlockPartRows.forEach((blockPartRow) => {
						let relations = [];
						resource.value.details.forEach((detail) => {
							detail.matrixRelationships.forEach((relationship) => {
								if (relationship.matrixRowId == blockPartRow.referenceId && blockPartRow.referenceType == 'matrixRow') {
									relations.push(relationship);
								}
							});
						});

						blockPartRows = [...blockPartRows, {
							...blockPartRow,
							relations,
						}];
					});
				});
			}
		});

		return {
			courses: courses,
			assessmentBlockPartRows: blockPartRows,
		};
	}, [form.getFieldState('assessmentBlocks')?.value]);

	const remainingBlocksPerCourse = useMemo(() => {
		const map = {};
		const assessmentBlocks = form.getFieldState('assessmentBlocks')?.value || [];
		assessmentBlocks.forEach(block => {
			block.resources?.forEach(resource => {
				const courseId = resource.value?.id;
				if (!courseId) return;
				map[courseId] = (map[courseId] || 0) + 1;
			});
		});
		return map;
	}, [form.getFieldState('assessmentBlocks')?.value]);

	/**
	 * @param {object} row
	 * @param {object} aspect
	 * @param {number} courseDetailsId
	 * @returns {object} coreContent
	 */
	const _generateCoreContent = (row, aspect, courseDetailsId) => {
		return {
			courseDetailsId,
			title: null,
			aspects: [
				{
					title: aspect.title,
					id: aspect.id,
					aspectRows: [{
						id: row.id,
						text: row.text,
					}],
				}
			],
		}
	}

	/**
	 * @param {object} row
	 * @param {object} aspect
	 * @param {number} courseDetailsId
	 * @param {boolean} isManual
	 */
	const toggleRow = (row, aspect, courseDetailsId, isManual = false) => {
		if (!isManual && lockedCoursesRef.current.has(courseDetailsId)) {
			console.log(`Matrix update ignored for locked course ${courseDetailsId}`);
			return false;
		}

		// If manual toggle, lock it in memory
		if (isManual) {
			lockedCoursesRef.current.add(courseDetailsId);
		}

		if (values.length == 0) {
			values.push({
				referenceType: props.referenceType,
				referenceId: props.referenceId,
				coreContents: [_generateCoreContent(row, aspect, courseDetailsId)],
			});

			input.onChange(values);
			if (props.onChange != null && typeof (props.onChange) == 'function') {
				props.onChange(values);
			}
			return true;
		}

		const isSelected = isRowSelected(row);
		values.forEach((value) => {
			let foundCoreContent = value.coreContents?.find((coreContent) =>
				coreContent.courseDetailsId == courseDetailsId
			);

			if (!foundCoreContent) {
				if (!value.coreContents) value.coreContents = [];
				value.coreContents.push(_generateCoreContent(row, aspect, courseDetailsId));
				return;
			}

			let fCCaspect = foundCoreContent.aspects.find((testAspect) =>
				testAspect.id === aspect.id
			);

			if (!fCCaspect) {
				// Add the aspect to the coreContent
				foundCoreContent.aspects.push({
					id: aspect.id,
					title: aspect.title,
					aspectRows: [{
						id: row.id,
						text: row.text,
					}],
				});
				return;
			}

			if (isSelected) {
				if (isManual) {
					fCCaspect.aspectRows = fCCaspect.aspectRows.filter((_row) => _row.id != row.id);
				}
				// If aspect now empty, remove that exact aspect
				if (fCCaspect.aspectRows.length == 0) {
					foundCoreContent.aspects = foundCoreContent.aspects.filter((_aspect) => _aspect.id != fCCaspect.id);
				}
				if (foundCoreContent.aspects.length == 0) {
					if (foundCoreContent.id !== undefined && foundCoreContent.id !== null) {
						value.coreContents = value.coreContents.filter((_coreContent) => _coreContent.id !== foundCoreContent.id);
					} else {
						value.coreContents = value.coreContents.filter((_coreContent) => _coreContent !== foundCoreContent);
					}
				}
				if (!value.coreContents || value.coreContents.length == 0) {
					values = values.filter((_value) => _value.referenceId != value.referenceId);
				}
			} else {
				if (!fCCaspect.aspectRows) fCCaspect.aspectRows = [];
				const alreadyExists = fCCaspect.aspectRows.some((_row) => _row.id === row.id);
				if (!alreadyExists) {
					fCCaspect.aspectRows.push({
						id: row.id,
						text: row.text,
					});
				}
			}
		});

		input.onChange(values);
		if (props.onChange != null && typeof (props.onChange) == 'function') {
			props.onChange(values);
		}
	};

	/**
	 * @param {object} row
	 * @returns {boolean} selected
	 */
	const isRowSelected = (row) => {
		if (input.value == null || input.value == "") {
			return false;
		}

		const inValues = input.value.find((value) => {
			return value.coreContents?.find((coreContent) => {
				return coreContent.aspects.find((aspect) => {
					return aspect.aspectRows.find((_row) => _row.id == row.id);
				});
			});
		});

		return inValues != null;
	}

	const cleanupCoreContentForCourse = () => {
		const existingCourseDetailsIds = new Set(
			courses.flatMap(course => course.details?.map(d => d.id) || [])
		);

		values = values.filter(value => {
			if (!value.coreContents) return true;
			value.coreContents = value.coreContents.filter(coreContent =>
				existingCourseDetailsIds.has(coreContent.courseDetailsId)
			);
			return value.coreContents.length > 0;
		});

		input.onChange(values);
		if (props.onChange) props.onChange(values);
	};

	useEffect(() => {
		lockedCoursesRef.current = new Set(
			values.flatMap(v => v.coreContents?.map(c => c.courseDetailsId) || [])
		);
	}, []);

	useEffect(() => {
		if (currentRows.current.length !== assessmentBlockPartRows?.length) {
			currentRows.current = assessmentBlockPartRows;

			assessmentBlockPartRows.forEach((blockPartRow) => {
				blockPartRow.relations.forEach((relation) => {
					courses.forEach((course) => {
						const coreContents = course.details[0]?.coreContent;

						if (coreContents == null) {
							return null;
						}

						coreContents.forEach((coreContent) => {
							coreContent.aspects.forEach((aspect) => {
								const aspectRow = aspect.aspectRows.find((aspectRow) =>
									aspectRow.id == relation.aspectRowId
								);

								if (aspectRow != null) {
									toggleRow(aspectRow, aspect, course.details[0].id);
								}
							});
						});
					});
				});
			});
		}
		cleanupCoreContentForCourse();
	}, [assessmentBlockPartRows, remainingBlocksPerCourse]);

	return (
		<div className="core-content">
			{courses.length > 0 ?
				courses.map((course) => {
					const coreContents = course.details[0]?.coreContent;

					if (coreContents == null) {
						return null;
					}

					return <Expandable
						title={course.title + " " + (course.year != null ? course.year : "") + " (" + course.courseCode + ") "}
						key={course.id}
						color={course.colorCode}
					>
						{coreContents.map((coreContent) => (
							<div key={coreContent.id}>
								<div>
									<h2>{coreContent.title}</h2>
								</div>

								{coreContent.aspects.map((aspect) => (
									<div key={'corecontent-aspects-' + aspect.id} className="aspects">
										<h3>{aspect.title != forbiddenText ? aspect.title : ''}</h3>

										{aspect.aspectRows.map((row) => {
											return <div key={'aspect-row-' + row.id} className="aspect-row">
												<Checkbox
													label={row.text}
													value={isRowSelected(row)}
													onChange={() => toggleRow(row, aspect, course.details[0].id, true)}
												/>
											</div>
										})}
									</div>
								))}
							</div>
						))}
					</Expandable>
				})
				: null}
		</div>
	);
}

BlockCoreContentField.defaultProps = {
	referenceId: 0,
	referenceType: "Planning",
};

export default BlockCoreContentField;